import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { i18nReady } from './i18n'
import { createAppRouter } from './router'
import { PartnerApiError } from './lib/api'
import { queryKeys } from './lib/queries'
import { safeReturnPath } from './lib/navigation'
import './styles/main.css'

/* Spec PARTNER-001 — entrada das telas do App da Organizacao Parceira. */

const PUBLIC_PATHS = ['/entrar', '/pedir-acesso', '/esqueci-a-senha']

/* Sessao expirada ou encerrada: volta para a entrada. Nas telas publicas o 401 e esperado
   (checagem "ja esta logado?") e nao redireciona. */
function onSessionError(error: unknown) {
    if (!(error instanceof PartnerApiError) || error.code !== 'PARTNER_SESSION_REQUIRED') return
    const onPublicPath = PUBLIC_PATHS.some(p => router.state.location.pathname.startsWith(p))
    if (onPublicPath) return
    queryClient.removeQueries({ queryKey: queryKeys.all })
    // PARTNER-002 D-12: depois de entrar de novo, volta para onde estava
    const voltar = safeReturnPath(router.state.location.href)
    void router.navigate({ to: '/entrar', search: voltar ? { voltar } : {}, replace: true })
}

const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: onSessionError }),
    mutationCache: new MutationCache({ onError: onSessionError }),
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: true,
            // Erro do cliente (4xx) nao melhora repetindo; rede e 5xx tentam mais uma vez.
            retry: (failureCount, error) => {
                if (error instanceof PartnerApiError && error.status >= 400 && error.status < 500) return false
                return failureCount < 1
            },
        },
        mutations: { retry: false },
    },
})

const router = createAppRouter(queryClient)

/* PARTNER-002 D-09: service worker so dos avisos. Aviso chegou com o app aberto: as telas atualizam na hora.
   Toque no aviso com o app ja aberto: navega aqui dentro (sem recarregar). */
if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as { type?: unknown; path?: unknown } | null
        if (data?.type === 'partner.push') void queryClient.invalidateQueries({ queryKey: queryKeys.all })
        if (data?.type === 'partner.navigate') {
            const path = safeReturnPath(data.path)
            if (path) router.history.push(path)
        }
    })
}

void i18nReady.then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
            </QueryClientProvider>
        </React.StrictMode>,
    )
})
